import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from "../../helpers/axiosInstance";
import { Button, Container } from "reactstrap";
import { useParams } from "react-router-dom";

const UpdateBlog = () => {
    const { id } = useParams();
    const [title, setTitle] = useState("");
    const [author, setAuthor] = useState("");
    const [coverImage, setCoverImage] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [images, setImages] = useState([]);
    const [imagesPreview, setImagesPreview] = useState([]);
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [newTag, setNewTag] = useState("");
    const [uploadErrors, setUploadErrors] = useState([]);
    const [existingImages, setExistingImages] = useState([]);

    // Fetch blog details for editing
    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const response = await axiosInstance.get(`/api/v1/blog/${id}`);
                const blog = response.data;
                if (blog.content) {
                    setContent(JSON.parse(blog.content));
                } else {
                    setContent('');
                }
                setTitle(blog.title);
                setAuthor(blog.author);
                setCoverImage(blog.coverImage);
                setCoverImagePreview(blog.coverImage);
                setExistingImages(blog.images || []);
            } catch (error) {
                console.error('Error fetching blog:', error);
            }
        };

        fetchBlog();
    }, [id]);

    // Handle file input changes
    const handleCoverImageChange = (e) => {
        const file = e.target.files ? e.target.files[0] : null;
        setCoverImage(file);
        setCoverImagePreview(URL.createObjectURL(file));
    };

    const handleImageChange = (e) => {
        const files = e.target.files;
        if (files) {
            const newImages = Array.from(files);
            const newPreviews = newImages.map(file => URL.createObjectURL(file));
            setImages(newImages);
            setImagesPreview(newPreviews);
        }
    };

    // Upload image to server
    const uploadImageToOm = async (file) => {
        try {
            const formData = new FormData();
            formData.append("image", file);
            const response = await axiosInstance.post(
                "api/v1/upload-image",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            return response.data.images;
        } catch (error) {
            console.error("Error uploading image:", error);
            return null;
        }
    };

   

 

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setUploadErrors([]);

        try {
            let coverImageUrl = coverImagePreview;
            if (coverImage) {
                coverImageUrl = await uploadImageToOm(coverImage);
                if (coverImageUrl === null) {
                    throw new Error("Failed to upload cover image");
                }
            }

            const uploadedImages = [];
            if (images.length > 0) {
                for (const image of images) {
                    const imageUrl = await uploadImageToOm(image);
                    if (imageUrl === null) {
                        throw new Error("Failed to upload an additional image");
                    }
                    uploadedImages.push(imageUrl);
                }
            }

            const bodyData = {
                title: title,
                author: author,
                content: JSON.stringify(content),
                coverImage: coverImageUrl,
                images: [...existingImages, ...uploadedImages], // Combine existing and new images
            };

            await axiosInstance.put(`/api/v1/admin/blog/${id}`, bodyData);
            alert("Blog updated successfully!");

        } catch (error) {
            console.error("Error updating blog:", error);
            setUploadErrors([error.message]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid>
            <div style={styles.container}>
                <h1 style={styles.header}>Update Blog</h1>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <label style={styles.label}>Blog Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        style={styles.input}
                        required
                    />

                    <label style={styles.label}>Author Name</label>
                    <input
                        type="text"
                        value={author}
                        style={styles.input}
                        onChange={(e) => setAuthor(e.target.value)}
                        required
                    />

                    <label style={styles.label}>Cover Image</label>
                    <input
                        type="file"
                        onChange={handleCoverImageChange}
                        style={styles.fileInput}
                        accept="image/*"
                    />
                    {coverImagePreview && (
                        <div style={styles.imagePreview}>
                            <strong>Cover Image Preview:</strong>
                            <img
                                src={coverImagePreview}
                                alt="Cover"
                                style={styles.image}
                            />
                        </div>
                    )}

                    <label style={styles.label}>Additional Images</label>
                    <input
                        type="file"
                        onChange={handleImageChange}
                        multiple
                        style={styles.fileInput}
                        accept="image/*"
                    />
                    {imagesPreview.length > 0 && (
                        <div style={styles.imagePreview}>
                            <strong>Additional Images Preview:</strong>
                            {imagesPreview.map((preview, index) => (
                                <img
                                    key={index}
                                    src={preview}
                                    alt={`Image ${index + 1}`}
                                    style={styles.image}
                                />
                            ))}
                        </div>
                    )}

                    <label style={styles.label}>Content</label>
                    <ReactQuill
                        value={content}
                        onChange={setContent}
                        modules={modules}
                        style={styles.quill}
                    />

                  


                    <Button
                        color="primary"
                        className="me-1"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? "Updating..." : "Update Blog"}
                    </Button>
                </form>
            </div>
        </Container>
    );
};

const styles = {
    container: {
        maxWidth: "1000px",
        margin: "50px auto",
        padding: "30px",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        boxShadow: "0 6px 12px rgba(0, 0, 0, 0.1)",
    },
    header: {
        marginBottom: "30px",
        color: "#333",
        fontWeight: "600",
        fontSize: "28px",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "25px",
    },
    label: {
        fontWeight: "600",
        fontSize: "16px",
        color: "#333",
    },
    input: {
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #ccc",
        fontSize: "16px",
        outline: "none",
        transition: "border 0.3s",
    },
    fileInput: {
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #ccc",
    },
    quill: {
        height: "250px",
        marginBottom: "20px",
    },
    addButton: {
        padding: "12px 18px",
        backgroundColor: "#007BFF",
        color: "#fff",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "16px",
        border: "none",
    },
    tagInputContainer: {
        display: "flex",
        alignItems: "center",
    },
    tagList: {
        marginTop: "15px",
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
    },
    tag: {
        backgroundColor: "#f1f1f1",
        padding: "8px 12px",
        borderRadius: "20px",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
    },
    removeButton: {
        marginLeft: "8px",
        cursor: "pointer",
        color: "#FF4D4D",
    },
    imagePreview: {
        margin: "15px 0",
    },
    image: {
        maxWidth: "120px",
        marginRight: "12px",
        borderRadius: "8px",
        border: "2px solid #ddd",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
};

const modules = {
    toolbar: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["bold", "italic", "underline"],
        ["image", "link"],
        ["clean"],
    ],
};

export default UpdateBlog;
